import { Link } from '@tanstack/react-router';
import { useBillingSummary, useCurrentUser, useFriendFeed, useMyTracksSummary } from '@/hook';
import { PageHeader, buttonClassName } from '@/component';
import { HomeFriends } from './HomeFriends';
import { HomePlanCard } from './HomePlanCard';
import { HomeTopQueued } from './HomeTopQueued';

const DAY_MS = 86_400_000;
const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "Tuesday evening" style eyebrow from the local clock. */
export function greetingEyebrow(now = new Date()) {
  const day = now.toLocaleDateString('en-US', { weekday: 'long' });
  const hour = now.getHours();
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  return `${day} ${part}`;
}

/** "10 tracks you made. 2 starter packs. 2 friends sang last night." The
 * middle clause says what the plan unlocks instead for a subscriber; the last
 * is left out when nobody sang. */
export function homeSubline(facts: { made: number; subscribed: boolean; starter: number; plan: number; friendsSang: number }): string {
  const packs = facts.subscribed ? `, ${plural(facts.plan, 'pack')} unlocked.` : `. ${plural(facts.starter, 'starter pack')}.`;
  const friends = facts.friendsSang > 0 ? ` ${plural(facts.friendsSang, 'friend')} sang last night.` : '';
  return `${plural(facts.made, 'track')} you made${packs}${friends}`;
}

/** Home: the greeting hero over a 1.5fr/1fr split. Top queued fills the left
 * column; Friends and the plan card the right. */
export function HomePage() {
  const { user } = useCurrentUser();
  const name = user?.displayName ?? user?.handle ?? 'singer';
  const made = useMyTracksSummary();
  const billing = useBillingSummary();
  const feed = useFriendFeed(5);
  const since = Date.now() - DAY_MS;
  const friendsSang = new Set((feed ?? []).filter((item) => item.sangAt >= since).map((item) => item.singer.userId)).size;

  return (
    <>
      <PageHeader
        eyebrow={greetingEyebrow()}
        title={`Welcome back, ${name}`}
        lede={
          billing && made
            ? homeSubline({ made: made.all, subscribed: billing.tier !== 'free', starter: billing.packs.perPlan.free, plan: billing.packs.perPlan[billing.tier], friendsSang })
            : undefined
        }
        action={
          <Link to="/create" className={buttonClassName({ className: 'px-[21px] py-[13px]' })}>
            Create karaoke track
          </Link>
        }
      />
      <div className="mt-[60px] grid grid-cols-1 items-start gap-14 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <HomeTopQueued />
        <div className="flex flex-col gap-12">
          <HomeFriends />
          <HomePlanCard />
        </div>
      </div>
    </>
  );
}
